/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,
	alertDuration: 6000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				// Refus metier (422 : stock, quantite max) : garder le message traduit de Shopify (A11Y-14)
				let message = `HTTP ${response.status}: ${response.statusText}`;
				try {
					const errorData = await response.json();
					message = errorData.description || errorData.message || message;
				} catch (parseError) {
					/* reponse non JSON : message HTTP conserve */
				}
				const error = new Error(message);
				error.status = response.status;
				throw error;
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			// Sections demandees (corps JSON, FormData ou ?sections= du GET) : completees par
			// ensureSections si l'API panier ne les renvoie pas (apercu admin)
			const sectionIds = isFormData ? body.get('sections') : body?.sections || new URLSearchParams(path.split('?')[1] || '').get('sections');
			if (sectionIds) await this.ensureSections(data, sectionIds);

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Complete data.sections quand l'API panier ne les renvoie pas.
	 * Dans l'apercu admin d'un theme non publie, cart/add.js, change.js et update.js
	 * renvoient sections: null et cart.js?sections= n'en renvoie aucune : seule l'API
	 * de rendu de sections sur une page (/?sections=) fonctionne. Aucune requete en
	 * plus sur le site en ligne, ou les sections sont deja dans la reponse.
	 * @param {Object} data - Reponse JSON de l'API panier, completee sur place
	 * @param {string|string[]} ids - Id(s) de section demandes
	 * @returns {Promise<Object>} data
	 */
	async ensureSections(data, ids) {
		const list = (Array.isArray(ids) ? ids : String(ids).split(',')).filter(Boolean);
		if (!data || !list.length || list.every(id => data.sections?.[id])) return data;

		try {
			// routes.root garde le prefixe de langue de l'URL
			const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${list.join(',')}`, {
				signal: AbortSignal.timeout(CART_CONFIG.apiTimeout)
			});
			if (response.ok) data.sections = { ...data.sections, ...(await response.json()) };
		} catch (error) {
			window.log('warn', 'Cart', 'Sections fallback failed', { error: error.message });
		}
		return data;
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Résoudre item_count : présent dans cart.js/change.js/update.js,
		// absent de cart/add.js — dans ce cas on le lit depuis le HTML de la section.
		let itemCount = data.item_count;
		if (itemCount === undefined && data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			const html = data.sections[sectionId];
			if (html) {
				const temp = document.createElement('div');
				temp.innerHTML = html;
				const countEl = temp.querySelector('[data-cart-count]');
				if (countEl) itemCount = parseInt(countEl.dataset.cartCount, 10) || 0;
			}
		}

		if (itemCount !== undefined) {
			DOM.cartBubbles.forEach(bubble => {
				bubble.classList.toggle('mm-none', itemCount === 0);
				bubble.classList.toggle('mm-flex-center', itemCount > 0);
				bubble.textContent = itemCount || '';
			});
			cartEmit('count-updated', { count: itemCount });
		}

		// Rafraîchir le DOM si sections présent (fonctionne aussi pour cart/add.js)
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			// Le remplacement du HTML détruit le bouton qui avait le focus (suppression, +/-) : le focus
			// tomberait sur body. On retient sa signature pour le rendre à son équivalent (WCAG 2.4.3).
			const active = document.activeElement;
			const hadFocus = currentCart.contains(active);
			let focusSelector = '';
			if (hadFocus) {
				const attr = ['data-cart-increase', 'data-cart-decrease', 'data-cart-remove'].find(a => active.hasAttribute(a));
				const cls = active.classList[0];
				if (attr) focusSelector = `[${attr}="${active.getAttribute(attr)}"]`;
				else if (cls && active.dataset.lineIndex) focusSelector = `.${CSS.escape(cls)}[data-line-index="${active.dataset.lineIndex}"]`;
			}
			currentCart.innerHTML = newCart.innerHTML;
			if (hadFocus) {
				// Repli si l'equivalent a disparu ou est desactive (ligne supprimee, + au stock max)
				const target = (focusSelector && currentCart.querySelector(`${focusSelector}:not([disabled])`))
					|| currentCart.querySelector('[data-cart-remove], .mm-dc-close-btn');
				target?.focus({ preventScroll: true });
			}
			// Reinitialize Splide sliders with auto-init
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element._splide) {
						try {
							const splideInstance = new Splide(element).mount();
							element._splide = splideInstance;
						} catch (error) {
							window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
	cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	// Dernier bouton d'ajout au panier : repli du focus a la fermeture (A11Y-21)
	lastAddButton: null,

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape') {
				e.preventDefault();
				this.close();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	/**
	 * Restore focus after close: trigger first, then the last add-to-cart button
	 * (the toaster "view cart" button is hidden once the drawer is open).
	 */
	restoreFocus(trigger) {
		for (const el of [trigger, this.lastAddButton]) {
			if (!el?.isConnected || el === document.body) continue;
			// Bouton du toaster en cours de masquage : focusable pendant le fondu, perdu ensuite
			if (el.closest('[data-toaster]:not(.is-active)')) continue;
			el.focus({ preventScroll: true });
			if (document.activeElement === el) return;
		}
	},

	/**
	 * Show and announce a cart error (A11Y-14): in the drawer if open, otherwise after the anchor.
	 */
	announceError(error, anchor) {
		const message = error?.status === 422 ? error.message : window.cartStrings?.error;
		if (!message) return;

		document.querySelectorAll('[data-cart-alert]').forEach(el => el.remove());

		const alert = document.createElement('p');
		alert.className = 'mm-cart-alert mm-mono-body-12';
		alert.setAttribute('role', 'alert');
		alert.setAttribute('data-cart-alert', '');
		alert.textContent = message;

		const drawer = document.querySelector(CART_CONFIG.selectors.drawerCart);
		const isOpen = DOM.cartWrapper?.classList.contains('mm-dc-is-open');
		if (isOpen && drawer) {
			drawer.prepend(alert);
		} else if (anchor) {
			anchor.insertAdjacentElement('afterend', alert);
		} else {
			return;
		}

		setTimeout(() => alert.remove(), CART_CONFIG.alertDuration);
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');
			html.classList.add('stop-scrolling');

			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, header, footer').forEach(el => {
				if (!wrapper.contains(el)) el.inert = true;
			});

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
			const trigger = triggerElement || document.activeElement;
			this.setupFocusTrap(wrapper, trigger);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		if (html) html.classList.remove('stop-scrolling');

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap, then give focus back to the trigger (WCAG 2.4.3, A11Y-21).
		// removeFocusTrap() vide triggerElement : le lire avant.
		const trigger = this.focusTrap.triggerElement;
		this.removeFocusTrap();
		this.restoreFocus(trigger);

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;
		
		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;
		
		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;
		
		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });
		
		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			
			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};
			
			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}
			
			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);
			
			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			this.announceError(error);
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);

		if (!freeProductsEl?.children.length) return data;

		// Lire l'état du panier depuis le HTML de la section (source de vérité Shopify),
		// pas depuis le DOM live qui peut être périmé au moment de l'appel.
		const sectionCartItems = temp.querySelectorAll('[data-cart-item-id]');
		const sectionVariantIds = new Set(Array.from(sectionCartItems).map(el => el.dataset.cartItemId));

		const updates = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			const hasIt = sectionVariantIds.has(variantId);

			if (hasIt && !shouldHave) {
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			} else if (!hasIt && shouldHave) {
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}
		});

		if (Object.keys(updates).length > 0) {
			try {
				const requestBody = { updates };
				if (sectionId) requestBody.sections = sectionId;
				const updatedData = await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
				return updatedData;
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		// Rafraîchit le DOM avec les données de la section déjà en mémoire
		CartAPI.refreshDOM(data.sections[sectionId]);
		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = window.debounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);
window.mmRefreshCartSection = () => {
	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
	if (!sectionId) return Promise.resolve();
	/* cart.js avec sections= renvoie item_count + total_price + sections HTML en une seule requête */
	return CartAPI.request('GET', `cart.js?sections=${sectionId}`)
		.then((data) => { if (data.sections?.[sectionId]) CartAPI.refreshDOM(data.sections[sectionId]); });
};

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });


// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			const btn = e.target.closest('[data-checkout-btn]');
			btn.classList.add('mm-loading');
			cartEmit('checkout-loading', { element: btn });
		}

	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			// Bouton reellement soumis (e.submitter) — sinon 1er submit du form.
			// Important : un form de card contient plusieurs submits (hover CTA desktop + CTA panel mobile).
			const submitButton = e.submitter || form.querySelector('[type="submit"]');
			const noToaster = form.hasAttribute('data-no-toaster');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				window.log('error', 'Cart', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);

				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response,
					noToaster,
				});

				Cart.lastAddButton = submitButton;

				// Rafraîchit le DOM + bubble, puis traite les free products sur les données fraîches
				await Cart.processFreeProducts(response);

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				window.log('error', 'Cart', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
				Cart.announceError(error, submitButton);
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
					// Le bouton desactive pendant la requete a perdu le focus : le lui rendre (A11Y-21)
					if (document.activeElement === document.body) submitButton.focus({ preventScroll: true });
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager.loadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
const initCart = () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();

	// Ecouteurs poses tout de suite, sans attendre cart.js : sinon, entre le chargement et la
	// reponse (ou si l'appel echoue), la fermeture du panier ne marche pas et un ajout au panier
	// part en POST natif vers /cart.
	initCartEventListeners();
	window.initAddToCartForms();

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
};

// readyState : le script peut s'executer apres DOMContentLoaded (injection tardive, app)
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCart);
} else {
	initCart();
}

